import { useCallback, useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { aiSessionContextPluginKey } from '../../../../pm-plugins/ai-session-context/ai-session-context-plugin-key';

const MARKDOWN_LIST_KEYS = ['-', '*', '•'];
const MODIFIER_KEYS = ['Shift', 'Alt'];

/**
 * Handles user discard actions such as clicking outside the container
 * or pressing the Escape key.
 *
 * When `lastTriggeredFrom` is 'spaceShortcut' and the space shortcut FG is on,
 * also allows early escape via space or markdown-list keys (hyphen, asterisk, bullet)
 * to close the modal and insert the typed character into the editor.
 */
export const useUserDiscard = ({
	ref,
	onDiscard,
	lastTriggeredFrom,
	editorView,
	disableClickOutside,
}: {
	/**
	 * When true, the click-away listener is not bound (Escape and the Stop button
	 * still cancel). Used to keep a background remix update alive when the user
	 * clicks in the editor, instead of discarding it.
	 */
	disableClickOutside?: boolean;
	editorView?: EditorView;
	lastTriggeredFrom?: string;
	onDiscard: (options?: { shouldIgnoreNextSpaceTrigger?: boolean }) => void;
	ref: React.MutableRefObject<HTMLDivElement | null>;
}): void => {
	const canEarlyEscape = useRef(true);

	const handleEscapeKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (
				canEarlyEscape.current &&
				event.key !== ' ' &&
				!MARKDOWN_LIST_KEYS.includes(event.key) &&
				!MODIFIER_KEYS.includes(event.key)
			) {
				canEarlyEscape.current = false;
			}

			if (
				!(
					event.key === 'Escape' ||
					(canEarlyEscape.current &&
						lastTriggeredFrom === 'spaceShortcut' &&
						(event.key === ' ' || MARKDOWN_LIST_KEYS.includes(event.key)))
				)
			) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			onDiscard?.({
				shouldIgnoreNextSpaceTrigger:
					(event.key === ' ' && lastTriggeredFrom === 'spaceShortcut') ||
					(event.key === 'Escape' && lastTriggeredFrom === 'spaceShortcut'),
			});

			if (editorView) {
				editorView.focus();
			}

			if (MARKDOWN_LIST_KEYS.includes(event.key) && editorView) {
				editorView.dispatch(editorView.state.tr.insertText(` ${event.key}`));
				return;
			}
			if (event.key === ' ' && lastTriggeredFrom === 'spaceShortcut' && editorView) {
				editorView.dispatch(editorView.state.tr.insertText(' '));
				return;
			}
		},
		[onDiscard, lastTriggeredFrom, editorView],
	);

	const handleEscapeKeyUp = useCallback((event: KeyboardEvent) => {
		if (event.key !== 'Escape') {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
	}, []);

	const handleClickOutside = useCallback(
		(event: MouseEvent) => {
			if (ref.current?.contains(event.target as Node)) {
				return;
			}
			// When an inline AI session is in progress, do not discard on outside click.
			// The user should be able to open Rovo Chat or other editor-adjacent UI
			// without losing the in-progress inline edit.
			if (editorView) {
				const sessionStatus = aiSessionContextPluginKey.getState(editorView.state)?.status;
				if (
					(sessionStatus === 'prompt' || sessionStatus === 'streaming') &&
					fg('cc-maui-phase-3')
				) {
					return;
				}
			}
			onDiscard?.();
		},
		[ref, onDiscard, editorView],
	);

	// Keyboard listeners own the focus call (focus is required to capture key events).
	// Kept separate from the click listener so toggling `disableClickOutside` mid-session
	// does not re-run this effect and steal focus back to the editor.
	useEffect(() => {
		const contentArea = document.querySelector<HTMLElement>('.ak-editor-content-area');
		if (!contentArea) {
			return;
		}
		// Ensure we focus the content area to capture keyboard events
		contentArea.focus({ preventScroll: true });
		return bindAll(contentArea, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
		]);
	}, [handleEscapeKeyDown, handleEscapeKeyUp]);

	// Click-away discard, bound on its own so it can be toggled via disableClickOutside
	// without re-running the focus effect above.
	useEffect(() => {
		if (disableClickOutside) {
			return;
		}
		const contentArea = document.querySelector<HTMLElement>('.ak-editor-content-area');
		if (!contentArea) {
			return;
		}
		return bindAll(contentArea, [{ type: 'click', listener: handleClickOutside }]);
	}, [handleClickOutside, disableClickOutside]);
};
